import {
  Box,
  Divider,
  IconButton,
  Modal,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { usePartnerConfigList } from '../Partner/Enrollment/usePartnerConfigList';
import { homeDepotPartnerId } from '../../../common/utils/PartnerEnrollmentUtils';
import { emailRegex } from '../../../common/constants';
import OptimusButton from '../../../common/library/Button/Button';
import OptimusInput from '../../../common/library/Input/Input';

interface HDEmailModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (email: string) => void;
}

export function HDEmailModal({ open, onClose, onSubmit }: HDEmailModalProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const partnerConfigList = usePartnerConfigList();
  const partnerLogoUrl = partnerConfigList
    .find((p) => p.id === homeDepotPartnerId)?.partnerLogoUrl;

  const {
    control, handleSubmit, reset, formState: { errors, isValid },
  } = useForm<{ email: string }>({ mode: 'onChange' });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onFormSubmit = ({ email }: { email: string }) => {
    onSubmit(email);
    reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onFormSubmit)}
        sx={{
          position: 'relative',
          backgroundColor: theme.palette.common.white,
          borderRadius: theme.spacing(1.5),
          width: theme.spacing(67.5),
          maxWidth: '90vw',
          outline: 'none',
          overflow: 'hidden',
          boxShadow: [
            '0px 20px 24px -4px rgba(10,13,18,0.08)',
            '0px 8px 8px -4px rgba(10,13,18,0.03)',
            '0px 3px 3px -1.5px rgba(10,13,18,0.04)',
          ].join(', '),
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            padding: theme.spacing(3),
            paddingRight: theme.spacing(6),
          }}
        >
          {partnerLogoUrl && (
            <Box
              sx={{
                width: theme.spacing(7.5),
                height: theme.spacing(7.5),
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: theme.spacing(1.25),
                flexShrink: 0,
                overflow: 'hidden',
                boxShadow: '0px 1px 2px 0px rgba(10,13,18,0.05)',
              }}
            >
              <Box
                component="img"
                src={partnerLogoUrl}
                alt="Home Depot Pro"
                sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>
          )}
          <Typography
            variant="h4"
            color={theme.palette.grey[900]}
            sx={{
              [theme.breakpoints.down('sm')]: {
                fontSize: theme.spacing(1.75),
              },
            }}
          >
            {t('homeDepot.emailModal.title')}
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              position: 'absolute',
              top: theme.spacing(2),
              right: theme.spacing(2),
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        {/* Body */}
        <Box
          sx={{
            padding: theme.spacing(3),
            paddingBottom: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1.25),
          }}
        >
          <Typography variant="body2" color={theme.palette.grey[600]}>
            {t('homeDepot.emailModal.label')}
          </Typography>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            rules={{
              required: true,
              pattern: {
                value: emailRegex,
                message: t('error.invalidEmail2') as string,
              },
            }}
            render={({ field }) => (
              <OptimusInput
                {...field}
                type="email"
                placeholder={t('homeDepot.emailModal.placeholder') as string}
                error={!!errors.email}
                helperText={errors.email?.message}
                sx={{ width: '100%' }}
              />
            )}
          />
        </Box>

        {/* Actions */}
        <Box
          sx={{
            padding: theme.spacing(3),
            paddingTop: theme.spacing(6),
          }}
        >
          <OptimusButton
            type="submit"
            customVariant="primary"
            buttonText={t('homeDepot.emailModal.submit') as string}
            disabled={!isValid}
            sx={{ width: '100%' }}
          />
        </Box>
      </Box>
    </Modal>
  );
}
